"use client";
import { AutocompleteInput } from "@/components/search/autocomplete-input";
import { useTalentSearchFilters } from "@/components/search/talent-search-filters-provider";
import type { PresetTypeSlug } from "@/features/presets";
import type { AutocompleteResult, SkillFilter } from "@/lib/api/types";
import { hasEntityForm } from "@/lib/search/entity-variants";
import { VariantsDropdown } from "../input/internal/variants-dropdown";
import { ClearChipsButton, FilterChip, FilterLabel, PresetButton } from "../shared";

export function FilterSkillsSection({ onOpenPreset }: { onOpenPreset?: (type: PresetTypeSlug) => void }) {
  const { filters, updateFilter } = useTalentSearchFilters();
  const skills = filters.skills ?? [];

  const add = (skill: SkillFilter) => {
    if (hasEntityForm(skills, skill.text ?? "")) return;
    updateFilter("skills", [...skills, skill]);
  };

  return (
    <>
      <div className="flex flex-col gap-1 w-full">
        <FilterLabel>Skills</FilterLabel>
        <div className="flex flex-col">
          <AutocompleteInput
            variant="filter"
            field="skills"
            placeholder="Search..."
            flatBottom
            onSelect={(result: AutocompleteResult) => {
              add({ canonical_id: result.canonical_id ?? undefined, text: result.value.toLowerCase(), required: false });
            }}
            onFreeText={(text: string) => {
              add({ text: text.toLowerCase(), required: false });
            }}
          />
          <PresetButton label="Skill Presets" onClick={() => onOpenPreset?.("skills")} />
        </div>
      </div>
      {skills.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {skills.map((sk, i) => {
            const excluded = sk.mode === "exclude";
            const required = sk.required ?? false;
            return (
              <FilterChip
                key={i}
                label={sk.text ?? ""}
                variants={sk.variants}
                beforeActions={
                  <VariantsDropdown
                    compact
                    field="skills"
                    item={sk}
                    onChange={(updated) => {
                      const next = [...skills];
                      next[i] = updated;
                      updateFilter("skills", next);
                    }}
                  />
                }
                excluded={excluded}
                required={required}
                onToggleExclude={() => {
                  const next = [...skills];
                  next[i] = { ...sk, mode: excluded ? "include" : "exclude" };
                  updateFilter("skills", next);
                }}
                onToggleRequired={() => {
                  const next = [...skills];
                  next[i] = { ...sk, required: !required };
                  updateFilter("skills", next);
                }}
                onRemove={() => updateFilter("skills", skills.filter((_, j) => j !== i))}
              />
            );
          })}
          <ClearChipsButton onClick={() => updateFilter("skills", undefined)} />
        </div>
      )}
    </>
  );
}
